import { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFilteredEvents } from '../hooks/useFilteredEvents';

const malakoffPostalCodes = [{ code_postal: '92240' }];
const malakoffPosition = { lat: 48.8198, lng: 2.299 };

const getWeekendRange = () => {
  const parisDate = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
      .formatToParts(new Date())
      .map(({ type, value }) => [type, value])
  );
  const today = new Date(Date.UTC(
    Number(parisDate.year),
    Number(parisDate.month) - 1,
    Number(parisDate.day)
  ));
  const dayOfWeek = today.getUTCDay();
  const daysUntilFriday = dayOfWeek === 6 ? -1 : dayOfWeek === 0 ? 0 : (5 - dayOfWeek + 7) % 7;
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() + daysUntilFriday);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + (dayOfWeek === 0 ? 0 : 2));

  const toDateString = (date) => date.toISOString().slice(0, 10);

  return {
    startDate: toDateString(start),
    endDate: toDateString(end)
  };
};

const formatDate = (date, language) => new Intl.DateTimeFormat(language, {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC'
}).format(new Date(`${date}T00:00:00Z`));

const MalakoffWeekendPage = () => {
  const { t } = useTranslation();
  const tFrench = useCallback(
    (key, options) => t(key, { ...options, lng: 'fr' }),
    [t]
  );
  const weekend = useMemo(getWeekendRange, []);
  const { events, loading, error } = useFilteredEvents({
    searchPosition: malakoffPosition,
    nearbyPostalCodes: malakoffPostalCodes,
    filterDate: weekend.startDate,
    filterEndDate: weekend.endDate
  });

  useEffect(() => {
    const previousTitle = document.title;
    const previousAttributes = [];
    const metadata = [
      ['meta[name="description"]', 'content', tFrench('malakoffWeekend.metaDescription')],
      ['meta[property="og:title"]', 'content', tFrench('malakoffWeekend.metaTitle')],
      ['meta[property="og:description"]', 'content', tFrench('malakoffWeekend.metaDescription')],
      ['meta[property="og:url"]', 'content', 'https://www.maploop.fr/malakoff/'],
      ['meta[name="twitter:title"]', 'content', tFrench('malakoffWeekend.metaTitle')],
      ['meta[name="twitter:description"]', 'content', tFrench('malakoffWeekend.metaDescription')],
      ['link[rel="canonical"]', 'href', 'https://www.maploop.fr/malakoff/']
    ];

    document.title = tFrench('malakoffWeekend.metaTitle');
    metadata.forEach(([selector, attribute, value]) => {
      const element = document.querySelector(selector);
      if (element) {
        previousAttributes.push([element, attribute, element.getAttribute(attribute)]);
        element.setAttribute(attribute, value);
      }
    });

    return () => {
      document.title = previousTitle;
      previousAttributes.forEach(([element, attribute, value]) => {
        if (value === null) {
          element.removeAttribute(attribute);
        } else {
          element.setAttribute(attribute, value);
        }
      });
    };
  }, [tFrench]);

  const dateRange = tFrench('malakoffWeekend.dateRange', {
    start: formatDate(weekend.startDate, 'fr-FR'),
    end: formatDate(weekend.endDate, 'fr-FR')
  });

  return (
    <main className="mx-auto w-full max-w-4xl px-4 pb-24 pt-8 sm:px-6">
      <header className="mb-8">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-bleu3">
          {tFrench('malakoffWeekend.eyebrow')}
        </p>
        <h1 className="mb-4 text-3xl font-bold leading-tight sm:text-4xl">
          {tFrench('malakoffWeekend.heading')}
        </h1>
        <p className="max-w-3xl text-base leading-relaxed text-gray-700">
          {tFrench('malakoffWeekend.intro')}
        </p>
      </header>

      <section aria-labelledby="malakoff-events-heading" className="mb-8">
        <div className="mb-4">
          <h2 id="malakoff-events-heading" className="text-2xl font-semibold">
            {tFrench('malakoffWeekend.eventsHeading')}
          </h2>
          <p className="mt-1 text-sm text-gray-600">{dateRange}</p>
        </div>

        {loading ? (
          <p role="status" className="rounded-lg bg-white p-5 text-gray-700 shadow-sm">
            {tFrench('malakoffWeekend.loading')}
          </p>
        ) : error ? (
          <p role="alert" className="rounded-lg bg-white p-5 text-gray-700 shadow-sm">
            {tFrench('malakoffWeekend.loadError')}
          </p>
        ) : events.length === 0 ? (
          <p className="rounded-lg bg-white p-5 text-gray-700 shadow-sm">
            {tFrench('malakoffWeekend.empty')}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {events.map((event) => (
              <li key={event.id}>
                <article className="h-full rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold">
                    <Link
                      to={`/event/${event.id}`}
                      className="text-bleu2 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bleu1"
                    >
                      {event.title}
                    </Link>
                  </h3>
                  <p className="mb-2 text-sm text-gray-700">
                    <time dateTime={event.start_date}>
                      {event.end_date && event.end_date !== event.start_date
                        ? tFrench('event.period', {
                            start: formatDate(event.start_date, 'fr-FR'),
                            end: formatDate(event.end_date, 'fr-FR')
                          })
                        : formatDate(event.start_date, 'fr-FR')}
                    </time>
                    {event.start_time && (
                      <span>
                        {' · '}
                        {event.start_time.slice(0, 5)}
                        {event.end_time && ` - ${event.end_time.slice(0, 5)}`}
                      </span>
                    )}
                  </p>
                  {(event.location_name || event.formatted_address || event.city) && (
                    <p className="mb-3 text-sm text-gray-600">
                      {[event.location_name, event.formatted_address, event.city]
                        .filter(Boolean)
                        .filter((value, index, values) => values.indexOf(value) === index)
                        .join(' · ')}
                    </p>
                  )}
                  {event.description && (
                    <p className="mb-4 text-sm leading-relaxed text-gray-700">
                      {event.description}
                    </p>
                  )}
                  <Link
                    to={`/event/${event.id}`}
                    className="inline-flex min-h-11 items-center font-medium text-bleu3 underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bleu1"
                  >
                    {tFrench('malakoffWeekend.eventDetails', { title: event.title })}
                  </Link>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-sm text-gray-600">
        <Link
          to="/"
          className="font-medium text-bleu3 underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bleu1"
        >
          {tFrench('malakoffWeekend.mapLink')}
        </Link>
      </p>
    </main>
  );
};

export default MalakoffWeekendPage;
